import {
  AdContextInterface,
  AdContextProviderProps,
  AdInterface,
  CurrentWarningMessageInterface
} from './types/ad';
import { CURRENT_AD_QUERY, GET_CONFIGURATIONS } from '../../queries';
import { CURRENT_TIME, FIVE_DAYS } from '../../../utils/constants';
import React, { createContext, useContext, useEffect, useReducer, useState } from 'react';
import { formsReducer, initialFormsState } from '../reducers/ad';
import { useHistory, useLocation } from 'react-router-dom';

import { EDIT_ROUTES } from '../../utils/adEditRoutes';
import { Loading } from '../../components/Loading';
import { MakeOptional } from '../../utils/make-optional';
import { getAdWithFilter } from '../../utils/get-listing-with-status';
import { getConfigurationsClient } from '../../services/Configurations.service';
import { getCurrentAd } from '../../services/Ad.service';
import { getIdFromURL } from '../../utils/getIdFromURL';
import { scrollWindowTo } from '../../utils/scroll';
import toastify from '../../lib/toastify';
import { useQuery } from '@tanstack/react-query';

export const AdContext = createContext<AdContextInterface | undefined>(undefined);

export function AdContextProvider({ children }: Readonly<AdContextProviderProps>): JSX.Element {
  const location = useLocation();
  const history = useHistory();
  const [formState, dispatchForm] = useReducer(formsReducer, initialFormsState);
  const [cumulativeChanges, setCumulativeChanges] = useState<MakeOptional<AdInterface>>();
  const [currentWarningMessage, setCurrentWarningMessage] = useState<
    CurrentWarningMessageInterface | undefined
  >(undefined);
  const [loading, setLoading] = useState(false);
  const [openEditView, setOpenEditView] = useState<string | undefined>(undefined);
  const [valueIsDifferentFromCurrent, setValueIsDifferentFromCurrent] = useState(false);
  const [submitOnModalClick, setSubmitOnModalClick] = useState(false);
  const [modalInfoChassi, setModalInfoChassi] = useState(false)
  const [transition, setTransition] = useState(false);
  const [currentTabDepth, setCurrentTabDepth] = useState(0);

  const idFromURL = Number(getIdFromURL(location.pathname));

  const { data, isLoading, isError } = useQuery({
    queryFn: () => getCurrentAd(idFromURL),
    enabled: !!idFromURL,
    queryKey: CURRENT_AD_QUERY(idFromURL)
  });

  useQuery({
    queryKey: [GET_CONFIGURATIONS],
    queryFn: () => getConfigurationsClient()
  });

  useEffect(() => {
    if (isLoading || loading) return;
    const routes = Object.keys(EDIT_ROUTES) as Array<keyof typeof EDIT_ROUTES>;
    const currentPath = routes.find(key => location.pathname.includes(EDIT_ROUTES[key].name));

    if (currentPath) {
      scrollWindowTo(0);
      return setOpenEditView(EDIT_ROUTES[currentPath].name);
    }

    setOpenEditView(undefined);
    setCumulativeChanges(undefined);
    setCurrentWarningMessage(undefined);
  }, [location.pathname, isLoading, loading]);

  useEffect(() => {
    if (!data) return;

    if (!data?.dataPublicacao) return;

    const postedAt = data.dataPublicacao;
    const postedDatePlusFiveDays = postedAt + FIVE_DAYS;
    const shouldShowFormsOnTimeDiff = CURRENT_TIME >= postedDatePlusFiveDays;
    if (!shouldShowFormsOnTimeDiff) return;
  }, [data]);

  if (isError) {
    toastify.error('falha na requisição', 'não foi possível buscar o anúncio neste momento');
    history.push('/meus-anuncios');
    return <></>;
  }

  if (!data || isLoading) {
    return <Loading backgroundOnly />;
  }

  return (
    <AdContext.Provider
      value={{
        loading,
        currentAd: getAdWithFilter(data),
        openEditView,
        formState,
        dispatchForm,
        setLoading,
        valueIsDifferentFromCurrent,
        setValueIsDifferentFromCurrent,
        submitOnModalClick,
        setSubmitOnModalClick,
        setTransition,
        transition,
        modalInfoChassi,
        setModalInfoChassi,
        currentWarningMessage,
        setCurrentWarningMessage,
        setCumulativeChanges,
        cumulativeChanges,
        currentTabDepth,
        setCurrentTabDepth
      }}
    >
      {children}
    </AdContext.Provider>
  );
}

export function useAdContext(): AdContextInterface {
  const context = useContext(AdContext);

  if (!context) {
    throw new Error('Ad is not available');
  }

  return context;
}
